<!--
  Component này được sử dụng để lựa chọn Grade, Class và Student
  Danh sách class được lấy từ API, trong mỗi class trả về sẽ có các grade tương ứng
  Danh sách grade được tạo ra từ danh sách class
  Danh sách student sẽ phụ thuộc vào danh sách class
  Có thể bỏ lựa chọn từng phần
 -->
<template>
  <v-form ref="form">
    <v-row>
      <v-col v-if="!disableGradeSelect" :cols="gradeCol.cols" :sm="gradeCol.sm" :md="gradeCol.md">
        <v-autocomplete
          v-model="selectedGrade"
          :items="grades"
          label="Khối"
          item-text="title"
          item-value="id"
          no-data-text="Danh sách trống"
          clearable
          clear-icon="mdi-close"
          dense
          outlined
          hide-details="auto"
          return-object
          :rules="gradeRules"
          :loading="classLoading"
          @blur="onGradeBlur"
        ></v-autocomplete>
      </v-col>
      <v-col v-if="!disableClassSelect" :cols="classCol.cols" :sm="classCol.sm" :md="classCol.md">
        <v-autocomplete
          v-model="selectedClass"
          :items="classes"
          label="Lớp"
          item-text="title"
          item-value="id"
          no-data-text="Danh sách trống"
          clearable
          clear-icon="mdi-close"
          dense
          outlined
          hide-details="auto"
          return-object
          :rules="classRules"
          :loading="classLoading"
          @change="onClassChange"
        ></v-autocomplete>
      </v-col>
      <v-col v-if="!disableStudentSelect" :cols="studentCol.cols" :sm="studentCol.sm" :md="studentCol.md">
        <v-autocomplete
          v-model="selectedStudent"
          :items="studentList"
          :filter="onStudentFilter"
          label="Học sinh"
          item-value="id"
          item-text="display"
          no-data-text="Danh sách trống"
          clearable
          clear-icon="mdi-close"
          dense
          outlined
          hide-details="auto"
          return-object
          :search-input.sync="searchStudentInput"
          :rules="studentRules"
          :loading="studentLoading"
          @change="onStudentChange"
        >
          <template #item="{ item }">
            <v-list-item-content style="max-width: 400px">
              <CardStudentInfo :student="item" />
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import utils from '@/plugins/utils.js'
import { textHelpers } from '@/helpers/TextHelper'
import { get, isArray, isEmpty, orderBy, debounce } from 'lodash'
import { Class, Student, Teaching } from '@/plugins/api'
import CardStudentInfo from '@/components/basic/card/CardStudentInfo.vue'
import { mapGetters } from 'vuex'

// Get all grades by classes
const getGradeListFromClassList = (classList = []) => {
  if (!isArray(classList) || isEmpty(classList)) {
    return []
  }
  return [...new Set(classList.filter(c => c.grade).map(c => c.grade))]
}

export default {
  components: {
    CardStudentInfo,
  },
  props: {
    defaultClass: {
      type: Object,
      default: () => {},
    },
    // Danh sách các class Id để fetch data
    // Dùng cho trường hợp login bằng tài khoản giáo viên
    // hoặc giới hạn số class được truy xuất dữ liệu
    searchClassIds: {
      type: Array,
      default: () => [],
    },
    disableGradeSelect: {
      type: Boolean,
      default: false,
    },
    disableClassSelect: {
      type: Boolean,
      default: false,
    },
    disableStudentSelect: {
      type: Boolean,
      default: false,
    },
    gradeCol: {
      type: Object,
      default: () => ({
        cols: 12,
        sm: 12,
        md: 4,
      }),
    },
    classCol: {
      type: Object,
      default: () => ({
        cols: 12,
        sm: 12,
        md: 4,
      }),
    },
    studentCol: {
      type: Object,
      default: () => ({
        cols: 12,
        sm: 12,
        md: 4,
      }),
    },
    gradeRules: {
      type: Array,
      default: () => [],
    },
    classRules: {
      type: Array,
      default: () => [],
    },
    studentRules: {
      type: Array,
      default: () => [],
    },
    autoSyncClassByStudent: {
      type: Boolean,
      default: true,
    },
    autoSyncGradeByClass: {
      type: Boolean,
      default: true,
    },
    allowFindAllStudent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selectedGrade: null,
    grades: [],
    selectedClass: null,
    classes: [], // Danh sách class để lựa chọn trên v-autocomplete
    allClasses: [], // Danh sách tất cả các class lấy về từ database
    selectedStudent: null,
    selectedDay: null,
    students: [],
    days: [],
    allStudents: [],
    searchStudentInput: '',
    classLoading: false,
    studentLoading: false,
    week: false,
    routeQuery: null,
  }),
  computed: {
    ...mapGetters('auth', ['ownClassIds', 'profile', 'role']),
    ...mapGetters('busDetail', ['weekdays']),
    studentList() {
      const students = orderBy(this.students, ['nameToNumber'], ['asc'])
      return [...students].filter(s => !!s && !!s.code).map(s => ({ ...s, display: `${s.name} [${s.code}]` }))
    },
    dayList() {
      const days = orderBy(this.days, Object.keys(this.weekdays))
      return days
    },
  },
  async created() {
    await this.fetchAllClasses()
    this.classes = [...this.allClasses]
    this.grades = getGradeListFromClassList(this.allClasses)

    if (this.role && this.role.type === 'teacher' && Array.isArray(this.grades) && this.grades.length > 0) {
      this.selectedGrade = this.grades[0]
    }
  },
  methods: {
    // Get all classes from database
    async fetchAllClasses() {
      this.classLoading = true
      try {
        const params = { _limit: -1 }
        // Get classes filtered by role and head-teacher
        if (this.role.type === 'teacher') {
          if (this.$route.path.includes('marks-input')) {
            // lop giang day
            if (this.profile.id) {
              const teachingParams = {
                teacher: this.profile.id,
                _limit: -1,
              }
              const teachings = await Teaching.fetch(teachingParams)
              const teachingClassIds = [...new Set(teachings.map(t => t.class && t.class.id))]

              params['id_in'] = teachingClassIds
            }
          } else if (this.ownClassIds) {
            // lop chu nhiem
            params['id_in'] = this.ownClassIds
          }
        }
        if (!isEmpty(this.searchClassIds)) {
          params['id_in'] = [...new Set([...params['id_in'], ...this.searchClassIds])]
        }
        const response = await Class.getClassWithGrade(params)
        this.allClasses = isArray(response) && !isEmpty(response) ? response : []
      } catch (error) {
        console.error(error)
      }
      this.classLoading = false
    },
    async fetchStudents() {
      if (this.disableStudentSelect) {
        return
      }
      this.studentLoading = true
      try {
        const params = {
          _sort: 'nameToNumber:ASC',
          _limit: 100,
        }
        if (!this.allowFindAllStudent) params['status'] = 'active'
        if (get(this.selectedClass, 'id')) {
          // Có lựa chọn class => Chọn danh sách student theo class
          params['currentClass'] = get(this.selectedClass, 'id')
        } else {
          // Không lựa chọn class => Chọn danh sách student theo danh sách class
          // if (this.classes.length) {
          //   params['currentClass_in'] = [...this.classes.map(c => c.id)]
          // }
        }
        this.students = await Student.getBasicStudentData(params)
      } catch (error) {
        console.error(error)
      }
      this.studentLoading = false
    },
    onClassChange(newClass) {
      // Hủy lựa chọn lớp => Hủy lựa chọn khối và học sinh
      if (!get(newClass, 'id', '')) {
        this.selectedGrade = null
        this.selectedStudent = null
      }
    },
    onStudentChange(newStudent) {},
    setClassById(classId) {
      const foundClass = this.classes.find(cls => cls.id === classId)
      if (foundClass) {
        this.selectedClass = foundClass
        this.onChange()
      }
    },
    onChange() {
      const data = {}
      if (!this.disableGradeSelect) {
        data.grade = { ...this.selectedGrade }
      }
      if (!this.disableClassSelect) {
        data.class = { ...this.selectedClass }
      }
      if (!this.disableStudentSelect) {
        data.student = { ...this.selectedStudent }
      }
      this.$emit('changed', data)
    },
    /**
     * Xử lý tình huống khi xóa lựa chọn khối mà đang chọn lớp mà lost focus khối
     * thì chọn lại khối theo lớp đã chọn
     */
    onGradeBlur() {
      if (get(this.selectedClass, 'grade.id')) {
        this.selectedGrade = this.selectedClass.grade
      }
    },
    onStudentFilter(item, queryText, itemText) {
      const searchInput = textHelpers.removeSpaces(utils.clearUnicode(queryText))
      return item.tags.includes(searchInput)
    },
    debounce: debounce(async (searchInput, context) => {
      if (!searchInput) {
        await context.fetchStudents()
        return
      }

      const params = {
        tags_contains: textHelpers.removeSpaces(utils.clearUnicode(searchInput)),
        _limit: 5,
        _sort: 'nameToNumber:ASC',
      }
      if (!context.allowFindAllStudent) params['status'] = 'active'
      if (get(context.selectedClass, 'id')) {
        // Có lựa chọn class => Chọn danh sách student theo class
        params['currentClass'] = get(context.selectedClass, 'id')
      } else {
        // Không lựa chọn class => Chọn danh sách student theo danh sách class
        params['currentClass_in'] = [...context.classes.map(c => c.id)]
      }
      context.studentLoading = true
      try {
        context.students = await Student.search(params)
      } catch (error) {
        console.error(error)
      }
      context.studentLoading = false
    }, 150),
    validate() {
      return this.$refs.form.validate()
    },
    reset() {
      this.selectedGrade = null
      this.selectedClass = null
      this.selectedStudent = null
      this.selectedDay = null
      this.week = false
    },
  },
  watch: {
    '$route.query': {
      handler(newValue) {
        if (newValue) {
          this.routeQuery = newValue
        }
      },
      immediate: true,
      deep: true,
    },
    selectedGrade: {
      handler(newSelectedGrade) {
        if (get(newSelectedGrade, 'id')) {
          // Lựa chọn khối mới => filter danh sách class theo khối
          this.classes = this.allClasses.filter(c => get(c, 'grade.id') === newSelectedGrade.id)
          /**
           * Xóa lựa chọn lớp khi chọn khối khác
           * Xử lý tình huống chọn sang grade khác nhưng vẫn đang select class của grade cũ
           * VD :
           * Grade : Khối 6 => Select class Lớp 6A
           * Chuyển sang Grade Khối 7 => class 6A không nằm trong danh sách class của khối 7 nhưng selectedClass vẫn đang là 6A
           * Khi chuyển về khối 6 => auto select lớp 6A
           * => Giải quyết : Xóa selectedClass nếu class được chọn không nằm trong khối đang chọn
           */
          if (get(this.selectedClass, 'grade.id', '') !== newSelectedGrade.id) {
            this.selectedClass = null
          }
        } else {
          // Hủy lựa chọn khối => Reset danh sách lớp
          this.classes = [...this.allClasses]
        }
        this.onChange()
      },
    },
    selectedClass: {
      handler(newSelectedClass) {
        if (get(newSelectedClass, 'id') && this.autoSyncGradeByClass) {
          // Lựa chọn lớp => Auto chọn khối theo lớp
          this.selectedGrade = get(newSelectedClass, 'grade')
          // Xóa lựa chọn học sinh khi chọn lớp khác
          if (get(this.selectedStudent, 'currentClass.id', '') !== newSelectedClass.id) {
            this.selectedStudent = null
          }
          this.fetchStudents()
        }
        this.onChange()
      },
    },
    defaultClass: {
      handler(newClass) {
        if (get(newClass, 'id')) {
          this.selectedClass = newClass
        }
      },
    },
    classes: {
      handler(newValue, oldValue) {
        if (newValue && (!oldValue || oldValue.length === 0)) {
          if (this.routeQuery && this.routeQuery.class) {
            this.setClassById(this.routeQuery.class)
            this.$emit('filter')
            this.routeQuery = null
          }
        }

        if (!get(this.selectedClass, 'id')) {
          this.fetchStudents()
        }
      },
    },
    selectedStudent: {
      handler(newSelectedStudent) {
        if (get(newSelectedStudent, 'id') && this.autoSyncClassByStudent) {
          // Lựa chọn học sinh => Auto chọn lớp theo học sinh
          if (get(this.selectedClass, 'id') !== get(newSelectedStudent, 'currentClass.id')) {
            this.selectedClass = this.allClasses.find(c => c.id === get(newSelectedStudent, 'currentClass.id'))
          }
        }
        this.onChange()
      },
    },
    searchStudentInput: {
      async handler(searchInput, oldInput) {
        // Handle first load
        if (!oldInput && !searchInput) {
          return
        }
        // Handle input change because of student selected
        if (
          get(this.selectedStudent, 'name') &&
          searchInput === `${this.selectedStudent.name} [${this.selectedStudent.code}]`
        ) {
          return
        }
        // Handle input change because of clear student selected by checking old input include [xxx] or not
        if (!get(this.selectedStudent, 'id', '') && /\[[^\]]+\]/.test(oldInput) && !searchInput) {
          return
        }
        this.debounce(searchInput, this)
      },
    },
  },
}
</script>
